<template>
  <sunset-page-shell>
    <div v-if="loading.page" class="legal-loading" aria-busy="true" aria-live="polite">
      <span class="legal-loading__spinner" aria-hidden="true" />
      <span class="legal-loading__text">Loading…</span>
    </div>
    <div v-else-if="error" class="legal-error" role="alert">
      <p class="legal-error__text">{{ error }}</p>
      <button type="button" class="legal-error__retry" @click="loadPage">Try again</button>
    </div>
    <article v-else class="legal-doc">
      <h1 class="legal-doc__title">{{ page.title }}</h1>
      <div class="legal-doc__body" v-html="page.content"></div>
    </article>
  </sunset-page-shell>
</template>

<script>
import SunsetPageShell from '@/components/SunsetPageShell.vue'
import PageService from '@/services/PageService'

export default {
  name: 'PageView',
  components: { SunsetPageShell },
  data() {
    return {
      loading: {
        page: true,
      },
      error: null,
      page: {
        title: '',
        content: '',
      },
    }
  },
  watch: {
    $route() {
      this.loadPage()
    },
  },
  mounted() {
    this.loadPage()
  },
  methods: {
    pageIdFromRoute() {
      const m = this.$route.meta
      if (m && m.cmsId != null) {
        return String(m.cmsId)
      }
      const id = this.$route.params.id
      return id != null ? String(id) : null
    },
    normalizePageResponse(data) {
      if (!data || typeof data !== 'object') {
        return null
      }
      if (data.page && data.page.title != null) {
        return data.page
      }
      if (data.data && data.data.page) {
        return data.data.page
      }
      return null
    },
    loadPage() {
      const id = this.pageIdFromRoute()
      if (!id) {
        this.$router.replace('/404')
        return
      }
      this.loading.page = true
      this.error = null
      this.page = { title: '', content: '' }
      PageService.getPage(id)
        .then((response) => {
          const page = this.normalizePageResponse(response.data)
          if (!page || (page.title == null && !page.content)) {
            this.loading.page = false
            this.error = 'This page could not be loaded from the server.'
            return
          }
          this.page = page
          this.loading.page = false
          document.title = 'Workmorr — ' + (page.title || 'Page')
        })
        .catch((err) => {
          this.loading.page = false
          const status = err.response && err.response.status
          if (status === 404) {
            this.$router.replace('/404')
            return
          }
          this.error =
            status === 401 || status === 403
              ? 'You do not have access to this page.'
              : 'We could not load this page from the server. Check that the API is available and try again.'
        })
    },
  },
}
</script>

<style scoped>
.legal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 40vh;
  color: #94a3b8;
}

.legal-loading__text {
  font-size: 0.95rem;
}

.legal-loading__spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #008d36;
  border-radius: 50%;
  animation: legal-spin 0.85s linear infinite;
}

@keyframes legal-spin {
  to {
    transform: rotate(360deg);
  }
}

.legal-error {
  padding: 2rem 0;
  text-align: center;
}

.legal-error__text {
  margin: 0 0 1rem;
  color: #94a3b8;
  line-height: 1.5;
}

.legal-error__retry {
  padding: 0.6rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #00b344 0%, #006b29 100%);
  border: none;
  border-radius: 999px;
  cursor: pointer;
}

.legal-error__retry:hover {
  filter: brightness(1.05);
}

.legal-doc__title {
  margin: 0 0 1.25rem;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #f5f7fa;
}

.legal-doc__body {
  font-size: 1rem;
  line-height: 1.65;
  color: #cbd5e1;
}

.legal-doc__body >>> h2 {
  margin: 1.75rem 0 0.65rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #f5f7fa;
}

.legal-doc__body >>> h2:first-child {
  margin-top: 0;
}

.legal-doc__body >>> p {
  margin: 0 0 1rem;
}

.legal-doc__body >>> a {
  color: #00b344;
  text-decoration: underline;
}

.legal-doc__body >>> a:hover {
  color: #34d37a;
}

.legal-doc__body >>> ul,
.legal-doc__body >>> ol {
  margin: 0 0 1rem 1.25rem;
  padding: 0;
}
</style>
